import React from 'react'
import { formatDate, formatTime } from '../../../common/utils/appUtils'
import { PAYMENT_MODE_OPTIONS, PRODUCT_DETAIL_FIELDS } from '../../../common/constant/page-constant'
import currencyFormatter from '../../../common/utils/currencyFormatterUtils'

const CustomerCopyTemplate = ({ invoiceData, branchData }) => {
  const getDiscountAmount = () => {
    if (invoiceData.discount && invoiceData.amount) {
      return (invoiceData.discount / 100) * invoiceData.amount
    }
    return 0
  }

  // VAT amount is now stored directly, not as percentage
  const getVatAmount = () => {
    return Number(invoiceData.vat || 0).toFixed(2)
  }

  const getVatPercentage = () => {
    if (invoiceData.vat_percentage !== undefined && invoiceData.vat_percentage !== null) {
      return invoiceData.vat_percentage
    }
    const baseAmount = (invoiceData.amount || 0) - getDiscountAmount()
    if (baseAmount > 0 && invoiceData.vat > 0) {
      return parseFloat(((invoiceData.vat / baseAmount) * 100).toFixed(2))
    }
    return 0
  }

  const getTotalQuantity = () => {
    return invoiceData.product_details?.reduce((sum, product) => {
      return sum + (product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0)
    }, 0) || 0
  }

  const getPaymentModeLabel = () => {
    const mode = PAYMENT_MODE_OPTIONS.find(item => item.name === invoiceData.payment_mode)
    return mode?.label || invoiceData.payment_mode || 'Cash'
  }

  // Check if this is an online sale
  const isOnlineSale = invoiceData.type === 'ONLINE'

  const styles = {
    container: {
      fontFamily: "'Poppins', 'Arial', sans-serif",
      maxWidth: '300px',
      margin: '0 auto',
      padding: '8px',
      backgroundColor: 'white',
      fontSize: '9px',
    },
    title: {
      textAlign: 'center',
      fontSize: '12px',
      fontWeight: 'bold',
      marginBottom: '8px',
      borderBottom: '1px solid #000',
      paddingBottom: '5px',
    },
    headerSection: {
      marginBottom: '8px',
      paddingBottom: '8px',
      borderBottom: '1px solid #000',
    },
    headerRow: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '5px',
    },
    logo: {
      width: '35px',
      height: '35px',
      marginRight: '8px',
      objectFit: 'contain',
    },
    branchInfo: {
      flex: 1,
    },
    branchNameRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    branchName: {
      fontSize: '11px',
      fontWeight: 'bold',
    },
    branchPhone: {
      fontSize: '9px',
    },
    branchAddress: {
      fontSize: '8px',
      marginTop: '2px',
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '8px',
      marginTop: '3px',
    },
    customerSection: {
      marginBottom: '8px',
      paddingBottom: '5px',
      borderBottom: '1px solid #000',
    },
    customerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '8px',
      marginBottom: '2px',
    },
    customerLabel: {
      fontWeight: 'bold',
      minWidth: '50px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '7px',
      marginBottom: '5px',
    },
    th: {
      border: '1px solid #000',
      padding: '3px 2px',
      textAlign: 'left',
      fontWeight: 'bold',
      backgroundColor: '#f5f5f5',
      fontSize: '7px',
    },
    thCenter: {
      border: '1px solid #000',
      padding: '3px 2px',
      textAlign: 'center',
      fontWeight: 'bold',
      backgroundColor: '#f5f5f5',
      fontSize: '7px',
    },
    thRight: {
      border: '1px solid #000',
      padding: '3px 2px',
      textAlign: 'right',
      fontWeight: 'bold',
      backgroundColor: '#f5f5f5',
      fontSize: '7px',
    },
    td: {
      border: '1px solid #000',
      padding: '2px',
      fontSize: '7px',
    },
    tdCenter: {
      border: '1px solid #000',
      padding: '2px',
      textAlign: 'center',
      fontSize: '7px',
    },
    tdRight: {
      border: '1px solid #000',
      padding: '2px',
      textAlign: 'right',
      fontSize: '7px',
    },
    totalRow: {
      fontWeight: 'bold',
      backgroundColor: '#f5f5f5',
    },
    summarySection: {
      marginTop: '5px',
      paddingTop: '5px',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '8px',
      padding: '2px 0',
    },
    netAmountRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '10px',
      fontWeight: 'bold',
      padding: '4px 0',
      borderTop: '1px solid #000',
      marginTop: '3px',
    },
    paymentSection: {
      marginTop: '8px',
      paddingTop: '5px',
      borderTop: '1px solid #000',
    },
    paymentRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '8px',
      padding: '2px 0',
    },
    disclaimer: {
      marginTop: '10px',
      paddingTop: '8px',
      borderTop: '1px solid #000',
      textAlign: 'center',
    },
    warningText: {
      fontSize: '8px',
      fontWeight: 'bold',
      marginBottom: '5px',
    },
    banglaText: {
      fontSize: '7px',
      lineHeight: '1.3',
      marginBottom: '8px',
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '8px',
      marginTop: '8px',
      paddingTop: '5px',
      borderTop: '1px solid #000',
    },
  }

  return (
    <div style={styles.container} className="invoice-template customer-copy">
      {/* Title */}
      <div style={styles.title}>SALES INVOICE</div>

      {/* Header Section */}
      <div style={styles.headerSection}>
        <div style={styles.headerRow}>
          {isOnlineSale ? (
            /* Online Sales - Show DG ePharma System Info */
            <div style={styles.branchInfo}>
              <div style={styles.branchNameRow}>
                <span style={styles.branchName}>DG ePharma</span>
                <span style={styles.branchPhone}>📞 09606501155</span>
              </div>
              <div style={styles.branchAddress}>
                📍 Online Order
              </div>
            </div>
          ) : (
            /* In-Store Sales - Show Branch Info */
            <>
              {branchData?.logo && (
                <img src={branchData.logo} alt="logo" style={styles.logo} />
              )}
              <div style={styles.branchInfo}>
                <div style={styles.branchNameRow}>
                  <span style={styles.branchName}>
                    {branchData?.pharmacy_name || invoiceData?.branch_details?.pharmacy_name || ''}
                    {(branchData?.name || invoiceData?.branch_details?.name) ? ` (${branchData?.name || invoiceData?.branch_details?.name})` : ''}
                  </span>
                  <span style={styles.branchPhone}>📞 {branchData?.mobile_no || invoiceData?.branch_details?.mobile_no}</span>
                </div>
                <div style={styles.branchAddress}>
                  📍 {branchData?.address || invoiceData?.branch_details?.address}
                </div>
              </div>
            </>
          )}
        </div>
        
        <div style={styles.infoRow}>
          <span><strong>Invoice:</strong> {invoiceData.invoice_number}</span>
          <span><strong>Date:</strong> {formatDate(invoiceData.updated_at || invoiceData.created_at)}</span>
        </div>
        <div style={styles.infoRow}>
          <span><strong>Served by:</strong> {invoiceData.served_by || invoiceData.created_by_name || 'Staff'}</span>
          <span><strong>Time:</strong> {formatTime(invoiceData.updated_at || invoiceData.created_at)}</span>
        </div>
      </div>

      {/* Customer Section */}
      <div style={styles.customerSection}>
        <div style={styles.customerRow}>
          <span style={styles.customerLabel}>{invoiceData?.customer?.name || 'Walk-in Customer'}</span>
          <span>📞 {invoiceData?.customer?.mobile_no || '-'}</span>
        </div>
        <div style={styles.customerRow}>
          <span>📍 {invoiceData?.customer?.shipping_address || invoiceData?.customer?.billing_address || '-'}</span>
        </div>
      </div>

      {/* Products Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>SL</th>
            <th style={styles.th}>Product Name (Size)-Strength</th>
            <th style={styles.th}>Category</th>
            <th style={styles.thRight}>Rate/Unit</th>
            <th style={styles.thCenter}>Qty</th>
            <th style={styles.thRight}>Total</th>
          </tr>
        </thead>
        <tbody>
          {invoiceData.product_details?.map((product, index) => {
            const qty = product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0
            const rate = product?.[PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT] || 0
            const amount = qty * rate
            const productName = product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] || ''
            const dose = product?.[PRODUCT_DETAIL_FIELDS.DOSE] || ''
            const strength = product?.strength || ''
            const category = product?.category || product?.product_category || ''
            
            return (
              <tr key={index}>
                <td style={styles.td}>{index + 1}</td>
                <td style={styles.td}>
                  {productName}{dose ? ` (${dose})` : ''}{strength ? `-${strength}` : ''}
                </td>
                <td style={styles.td}>{category}</td>
                <td style={styles.tdRight}>{Number(rate).toFixed(2)}</td>
                <td style={styles.tdCenter}>{qty}</td>
                <td style={styles.tdRight}>{Number(amount).toFixed(2)}</td>
              </tr>
            )
          })}
          {/* Total Row */}
          <tr style={styles.totalRow}>
            <td style={styles.td} colSpan={4}><strong>Total</strong></td>
            <td style={styles.tdCenter}><strong>{getTotalQuantity()}</strong></td>
            <td style={styles.tdRight}><strong>{Number(invoiceData.amount || 0).toFixed(2)}</strong></td>
          </tr>
        </tbody>
      </table>

      {/* Summary Section */}
      <div style={styles.summarySection}>
        <div style={styles.summaryRow}>
          <span><strong>Discount</strong></span>
          <span>{Number(getDiscountAmount() || 0).toFixed(2)}</span>
        </div>
        <div style={styles.summaryRow}>
          <span><strong>VAT ({getVatPercentage()}%)</strong></span>
          <span>{getVatAmount()}</span>
        </div>
        <div style={styles.netAmountRow}>
          <span>Net Amount (BDT)</span>
          <span>{Number(invoiceData.total_amount || 0).toFixed(2)}</span>
        </div>
        
        {/* Payment Details - Same column alignment */}
        <div style={styles.summaryRow}>
          <span>Payment Mode</span>
          <span>{getPaymentModeLabel()}</span>
        </div>
        {invoiceData.payment_mode === 'CASH' && (
          <div style={styles.summaryRow}>
            <span>Paid by Cash</span>
            <span>{Number(invoiceData.paid_amount || invoiceData.total_amount || 0).toFixed(2)}</span>
          </div>
        )}
        {invoiceData.payment_mode === 'CARD' && (
          <>
            {invoiceData.transaction_fee_percent > 0 && (
              <div style={styles.summaryRow}>
                <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                <span>{Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}</span>
              </div>
            )}
            {invoiceData.final_card_amount && invoiceData.transaction_fee_amount > 0 && (
              <div style={styles.summaryRow}>
                <span>Final Card Amt</span>
                <span>{Number(invoiceData.final_card_amount || 0).toFixed(2)}</span>
              </div>
            )}
            {invoiceData.total_payable && invoiceData.transaction_fee_amount > 0 && (
              <div style={{...styles.summaryRow, fontWeight: 'bold'}}>
                <span>Total Payable</span>
                <span>{Number(invoiceData.total_payable || 0).toFixed(2)}</span>
              </div>
            )}
          </>
        )}
        {invoiceData.payment_mode === 'ONLINE' && (
          <>
            {invoiceData.mfs_name && (
              <div style={styles.summaryRow}>
                <span>MFS</span>
                <span>{invoiceData.mfs_name}</span>
              </div>
            )}
            {invoiceData.transaction_fee_percent > 0 && (
              <div style={styles.summaryRow}>
                <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                <span>{Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}</span>
              </div>
            )}
            {invoiceData.total_payable && invoiceData.transaction_fee_amount > 0 && (
              <div style={{...styles.summaryRow, fontWeight: 'bold'}}>
                <span>Total Payable</span>
                <span>{Number(invoiceData.total_payable || 0).toFixed(2)}</span>
              </div>
            )}
          </>
        )}
        {invoiceData.payment_mode === 'CASH_CARD' && (
          <>
            <div style={styles.summaryRow}>
              <span>Paid by Cash</span>
              <span>{Number(invoiceData.amount_cash || 0).toFixed(2)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Paid by Card</span>
              <span>{Number(invoiceData.amount_card || 0).toFixed(2)}</span>
            </div>
            {invoiceData.transaction_fee_percent > 0 && (
              <div style={styles.summaryRow}>
                <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                <span>{Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}</span>
              </div>
            )}
            {invoiceData.final_card_amount && invoiceData.transaction_fee_amount > 0 && (
              <div style={styles.summaryRow}>
                <span>Final Card Amt</span>
                <span>{Number(invoiceData.final_card_amount || 0).toFixed(2)}</span>
              </div>
            )}
            {invoiceData.total_payable && invoiceData.transaction_fee_amount > 0 && (
              <div style={{...styles.summaryRow, fontWeight: 'bold'}}>
                <span>Total Payable</span>
                <span>{Number(invoiceData.total_payable || 0).toFixed(2)}</span>
              </div>
            )}
          </>
        )}
        {invoiceData.payment_mode === 'DUE_SALES' && (
          <div style={styles.summaryRow}>
            <span>Due Amount</span>
            <span>{Number(invoiceData.total_amount || 0).toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <div style={styles.disclaimer}>
        {isOnlineSale ? (
          /* Online Sales - Different disclaimer */
          <div style={styles.warningText}>
            "SOLD ITEMS CAN NOT BE EXCHANGED/RETURNED"
          </div>
        ) : (
          /* In-Store Sales - Full disclaimer with Bangla text */
          <>
            <div style={styles.warningText}>
              "SOLD ITEM CAN NOT BE EXCHANGED<br />WITHOUT INVOICE"
            </div>
            <div style={styles.banglaText}>
              (বিঃদ্রঃ পাতা কাটা ওষুধ, ইন্সুলিন, ভ্যাক্সিন, ও সুগার টেস্ট স্ট্রিপ<br />
              ফেরত বা পরিবর্তন করা হয় না।)
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <span>Powered by: DG ePharma</span>
        <span>📞 0936846398</span>
      </div>
    </div>
  )
}

export default CustomerCopyTemplate
